import React, { ReactNode } from 'react'

import type { DraggableSyntheticListeners } from '@dnd-kit/core'
import type { SxProps } from '@mui/material/styles'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'

export interface DndBlockProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  disabled?: boolean
  editable: boolean
  orientation?: 'horizontal' | 'vertical' | 'grid'
  listeners?: DraggableSyntheticListeners
  sx?: SxProps
}

const DndBlock = React.forwardRef((props: DndBlockProps, ref: any) => {
  const {
    children,
    disabled,
    editable,
    listeners,
    orientation = 'vertical',
    sx,
    ...rest
  } = props

  const isHorizontal = orientation === 'horizontal' || orientation === 'grid'

  return (
    <Box
      {...rest}
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: isHorizontal ? 'column' : 'row',
        opacity: disabled ? 0.5 : undefined,
        pointerEvents: disabled ? 'none' : undefined,
        position: 'relative',
        minWidth: 0,
        width: orientation === 'grid' ? undefined : '100%',
        ...(editable
          ? {
              ['&:hover']: {
                ['& .DragHandleContainer']: {
                  opacity: 1
                }
              }
            }
          : null),
        ...sx
      }}
    >
      {editable ? (
        <Box
          {...listeners}
          className="DragHandleContainer"
          sx={{
            alignItems: 'center',
            color: 'action.disabled',
            cursor: disabled ? 'not-allowed' : 'grab',
            display: 'flex',
            height: !isHorizontal ? '100%' : undefined,
            justifyContent: 'center',
            left: !isHorizontal ? -24 : undefined,
            opacity: 0,
            position: 'absolute',
            top: isHorizontal ? -24 : undefined,
            width: isHorizontal ? '100%' : undefined,
            zIndex: 2,
            ['&:active']: !disabled
              ? {
                  cursor: 'grabbing'
                }
              : undefined,
            ['&:hover']: !disabled
              ? {
                  color: 'primary.main'
                }
              : undefined
          }}
        >
          <Icon
            name={isHorizontal ? 'DragHandleHorizontal' : 'DragHandleVertical'}
          />
        </Box>
      ) : null}
      {isHorizontal ? (
        children
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      )}
    </Box>
  )
})

DndBlock.displayName = 'DndBlock'
export default DndBlock
